import styled from '@emotion/styled'
import { faCheckCircle, faClock, faExclamationTriangle, faSync, faTrophy } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import DashboardCard from '../components/DashboardCard'
import SkeletonLoader from '../components/SkeletonLoader'
import StatCard from '../components/StatCard'
import { BadnetStatsData } from '../types/widget-data.type'

const SyncStatusContainer = styled.div<{ status: 'synced' | 'syncing' | 'error' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    margin-top: 16px;
    background: ${({ status }) => {
        switch (status) {
            case 'synced':
                return '#f0fdf4'
            case 'syncing':
                return '#fefce8'
            case 'error':
                return '#fef2f2'
            default:
                return '#f3f4f6'
        }
    }};
`

const SyncStatusIcon = styled.div<{ status: 'synced' | 'syncing' | 'error' }>`
    font-size: 1.6rem;
    color: ${({ status }) => {
        switch (status) {
            case 'synced':
                return '#16a34a'
            case 'syncing':
                return '#ca8a04'
            case 'error':
                return '#dc2626'
            default:
                return '#6b7280'
        }
    }};
    animation: ${({ status }) => (status === 'syncing' ? 'spin 1s linear infinite' : 'none')};

    @keyframes spin {
        from {
            transform: rotate(0deg);
        }
        to {
            transform: rotate(360deg);
        }
    }
`

const SyncStatusText = styled.p<{ status: 'synced' | 'syncing' | 'error' }>`
    font-size: 1.4rem;
    margin: 0;
    color: ${({ status }) => {
        switch (status) {
            case 'synced':
                return '#16a34a'
            case 'syncing':
                return '#ca8a04'
            case 'error':
                return '#dc2626'
            default:
                return '#6b7280'
        }
    }};
    font-weight: 500;
`

const BadnetStatsWidget = () => {
    const { t } = useTranslation()

    const { data: tournaments, isLoading } = useQuery({
        queryKey: ['badnet-tournaments'],
        queryFn: async () => {
            const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/badnet-tournaments`)
            return response.json()
        },
    })

    if (isLoading) {
        return <SkeletonLoader showHeader itemCount={2} itemHeight="60px" />
    }

    const tournamentsArray = Array.isArray(tournaments) ? tournaments : []

    const data: BadnetStatsData = {
        upcomingTournaments: tournamentsArray.filter((t: any) => t.status === 'upcoming').length || 0,
        queueTournaments: tournamentsArray.filter((t: any) => t.status === 'queue').length || 0,
        syncStatus: 'synced',
    }

    const getSyncIcon = () => {
        switch (data.syncStatus) {
            case 'synced':
                return <FontAwesomeIcon icon={faCheckCircle} />
            case 'syncing':
                return <FontAwesomeIcon icon={faSync} />
            case 'error':
                return <FontAwesomeIcon icon={faExclamationTriangle} />
            default:
                return <FontAwesomeIcon icon={faSync} />
        }
    }

    return (
        <DashboardCard title={t('adminDashboard.widgets.badnetStats.title')} icon={<FontAwesomeIcon icon={faTrophy} />}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                <StatCard
                    label={t('adminDashboard.widgets.badnetStats.upcoming')}
                    value={data.upcomingTournaments}
                    icon={<FontAwesomeIcon icon={faTrophy} />}
                    color="blue"
                />
                <StatCard
                    label={t('adminDashboard.widgets.badnetStats.queue')}
                    value={data.queueTournaments}
                    icon={<FontAwesomeIcon icon={faClock} />}
                    color="green"
                />
            </div>
            <SyncStatusContainer status={data.syncStatus}>
                <SyncStatusIcon status={data.syncStatus}>{getSyncIcon()}</SyncStatusIcon>
                <SyncStatusText status={data.syncStatus}>
                    {t('adminDashboard.widgets.badnetStats.syncStatus')}
                </SyncStatusText>
            </SyncStatusContainer>
        </DashboardCard>
    )
}

export default BadnetStatsWidget
